import { useIntl } from 'react-intl';
import { BarChart3 } from 'lucide-react';

import Tab from 'src/componentes/tabs/tab';
import Tabs from 'src/componentes/tabs/tabs';
import EstatisticasCards from './EstatisticasCards';
import LikertDashboard from './LikertDashboard';
import NpsDashboard from './NpsDashboard';
import PerguntasDashboard from './PerguntasDashboard';
import { obterPerguntasComOpcoes } from './perguntas-dashboard/utils';
import useDashboardEvolucaoFiltro from './useDashboardEvolucaoFiltro';

const emptyStateStyle = {
  alignItems: 'center',
  color: '#98a2b3',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  justifyContent: 'center',
  padding: '48px 16px',
  textAlign: 'center',
};

const toNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

function DashboardConteudo({
  aplicacoes = [],
  dados,
  dadosLikert,
  dadosPerguntas,
  entidadeId,
  estatisticas,
  estatisticasIsLoading,
  filtros,
  recorrente = false,
  refreshToken,
}) {
  const intl = useIntl();
  const filtroEvolucao = useDashboardEvolucaoFiltro(aplicacoes);

  const renderEmptyState = (mensagem) => (
    <div style={emptyStateStyle}>
      <BarChart3 size={40} strokeWidth={1.5} />
      <div>
        {intl.formatMessage(
          typeof mensagem === 'string' ? { id: mensagem } : mensagem
        )}
      </div>
    </div>
  );

  const renderResultadoNps = () => {
    if (!dados) return null;

    const geral = dados.geral || dados;

    const semRespostas = toNumber(geral.totalRespostas) === 0;
    if (semRespostas && !recorrente) {
      return renderEmptyState('labelDashboardNpsSemRespostas');
    }

    return (
      <NpsDashboard
        dados={dados}
        entidadeId={entidadeId}
        filtroEvolucao={filtroEvolucao}
        filtros={filtros}
        recorrente={recorrente}
        refreshToken={refreshToken}
        semRespostas={semRespostas}
      />
    );
  };

  const renderResultadoLikert = () => {
    const geral = dadosLikert?.geral || dadosLikert;

    const semRespostas = toNumber(geral?.totalRespostas) === 0;
    if (semRespostas && !recorrente) {
      return renderEmptyState({
        id: 'labelDashboardLikertSemRespostas',
      });
    }

    return (
      <LikertDashboard
        dados={dadosLikert}
        entidadeId={entidadeId}
        filtroEvolucao={filtroEvolucao}
        filtros={filtros}
        recorrente={recorrente}
        refreshToken={refreshToken}
        semRespostas={semRespostas}
      />
    );
  };

  const geralNps = dados?.geral || dados;
  const possuiPerguntasNps =
    !!dados?.possuiPerguntasNps ||
    !!geralNps?.possuiPerguntasNps ||
    toNumber(dados?.quantidadePerguntasNps) > 0 ||
    toNumber(geralNps?.quantidadePerguntasNps) > 0 ||
    (Array.isArray(dados?.perguntas) && dados.perguntas.length > 0);

  const geralLikert = dadosLikert?.geral || dadosLikert;
  const possuiPerguntasLikert =
    !!dadosLikert?.possuiPerguntasLikert ||
    !!geralLikert?.possuiPerguntasLikert ||
    toNumber(dadosLikert?.quantidadePerguntasLikert) > 0 ||
    toNumber(geralLikert?.quantidadePerguntasLikert) > 0;

  const perguntasComOpcoes = obterPerguntasComOpcoes(dadosPerguntas);
  const possuiPerguntasComOpcoes =
    recorrente ||
    !!dadosPerguntas?.possuiPerguntas ||
    perguntasComOpcoes.length > 0;

  const renderResultadoPerguntas = () => {
    const semRespostas = toNumber(dadosPerguntas?.totalRespostas) === 0;
    if (semRespostas && !recorrente) {
      return renderEmptyState('labelNenhumaRespostaParaExibir');
    }

    return (
      <PerguntasDashboard
        dados={dadosPerguntas}
        entidadeId={entidadeId}
        filtroEvolucao={filtroEvolucao}
        filtros={filtros}
        recorrente={recorrente}
        refreshToken={refreshToken}
        semRespostas={semRespostas}
      />
    );
  };

  return (
    <>
      <EstatisticasCards
        estatisticas={estatisticas}
        isLoading={estatisticasIsLoading}
      />
      {(possuiPerguntasNps ||
        possuiPerguntasLikert ||
        possuiPerguntasComOpcoes) && (
        <Tabs paddingContent="18px 0 0">
          {possuiPerguntasNps && (
            <Tab label={intl.formatMessage({ id: 'labelNps' })}>
              {renderResultadoNps()}
            </Tab>
          )}
          {possuiPerguntasLikert && (
            <Tab
              label={intl.formatMessage({
                id: 'labelLikert',
              })}
            >
              {renderResultadoLikert()}
            </Tab>
          )}
          {possuiPerguntasComOpcoes && (
            <Tab label={intl.formatMessage({ id: 'labelPerguntas' })}>
              {renderResultadoPerguntas()}
            </Tab>
          )}
        </Tabs>
      )}
    </>
  );
}

export default DashboardConteudo;
